import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  ChevronLeft,
  Heart,
  RefreshCw,
  Share2,
  Sliders,
  Sparkles,
  X,
} from "lucide-react";
import { Eyebrow, Wordmark } from "@/components/editorial";
import { ApiError, api } from "@/lib/api";
import { useFavorites } from "@/features/favorites/useFavorites";
import { useEntitlements, usePaywall } from "@/features/paywall";
import { BrigadeProgress } from "@/features/menu/BrigadeProgress";
import { PremiumFiltersSheet } from "@/features/menu/PremiumFiltersSheet";
import { SwapPendingBanner } from "@/features/menu/SwapPendingBanner";
import { useMealSwap } from "@/features/menu/useMealSwap";
import { ThemeSelectionSheet } from "@/features/menu/ThemeSelectionSheet";
import {
  getNextWeekThemePreference,
  getNextWeekStart,
  setNextWeekThemePreference,
} from "@/features/menu/themePreference";
import { shareMenu } from "@/lib/share";
import { getFoodImageUrl } from "@/lib/stock-food-images";
import {
  DAY_LABELS_SHORT,
  formatPrice,
  formatWeekRange,
  nutriColor,
  parseISODate,
} from "@/features/menu/menuLabels";
import { useMenu } from "@/features/menu/store";
import { useBrigadeStream } from "@/hooks/useBrigadeStream";
import {
  useNextWeekMenuPlan,
  MENU_NEXT_KEY,
} from "@/hooks/useNextWeekMenuPlan";
import { MENU_LATEST_KEY } from "@/hooks/useMenuPlan";
import { cn } from "@/lib/utils";
import type {
  MenuPlan,
  NutriScore,
  PremiumFilters,
  Recipe,
  ValidatedMenu,
} from "@/types/menu";

/**
 * Next-week menu page — mirrors MenuPage but sources from nextWeekPlan.
 *
 * Generation flow (when ?generate=true):
 *   1. Brigade runs (SSE stream)
 *   2. On completion → write to main plan for reveal → navigate /menu/reveal
 *   3. Reveal flow → /menu/next?validate=true
 *   4. Validate → write to nextWeekPlan + clear main plan
 */
export function MenuNextPage() {
  const { plan: nextWeekPlan } = useNextWeekMenuPlan();

  // Main store's plan holds the brigade draft during the reveal→validate flow.
  const mainPlan = useMenu((s) => s.plan);
  const setPlan = useMenu((s) => s.setPlan);
  const clearPlan = useMenu((s) => s.clearPlan);
  const setNextWeekPlan = useMenu((s) => s.setNextWeekPlan);

  const paywall = usePaywall();
  const { isPremium } = useEntitlements();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();

  const [filters, setFilters] = useState<PremiumFilters | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [theme, setTheme] = useState<string | null>(() =>
    getNextWeekThemePreference(),
  );
  const [themeSheetOpen, setThemeSheetOpen] = useState(false);

  function handleThemeSelect(themeId: string | null) {
    setTheme(themeId);
    setNextWeekThemePreference(themeId);
  }

  const genMode: "personalised" | "surprise" = (() => {
    const stored = localStorage.getItem("bc.generation_mode");
    return stored === "personalised" || stored === "surprise"
      ? stored
      : "personalised";
  })();

  const brigade = useBrigadeStream();
  const { isFavorite, toggle: toggleFav } = useFavorites();

  // When brigade completes, write to main store and go to reveal
  useEffect(() => {
    if (brigade.plan) {
      setPlan(brigade.plan);
      navigate("/menu/reveal");
    }
  }, [brigade.plan, setPlan, navigate]);

  // Auto-trigger generation when ?generate=true
  useEffect(() => {
    if (
      params.get("generate") === "true" &&
      !nextWeekPlan &&
      !brigade.running
    ) {
      setParams({}, { replace: true });
      void brigade.start({
        generation_mode: genMode,
        theme: getNextWeekThemePreference(),
        week_start: getNextWeekStart(),
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // The plan to validate is the main store's draft (written by brigade→reveal).
  // nextWeekPlan is what we display once validated.
  const draftPlan = mainPlan?.status !== "validated" ? mainPlan : null;

  const validate = useMutation({
    mutationFn: (args: {
      draftSessionId?: string | null;
      silent?: boolean;
    }) => {
      const qs = args.draftSessionId
        ? `?draft_session_id=${encodeURIComponent(args.draftSessionId)}`
        : "";
      return api<ValidatedMenu>(`/api/v1/menu/validate${qs}`, {
        method: "POST",
        body: draftPlan,
      }).then((res) => ({ res, silent: args.silent ?? false }));
    },
    onSuccess: ({ res, silent }) => {
      // Write to next-week store
      setNextWeekPlan(res.plan, res.menu_id ?? null);
      queryClient.setQueryData(MENU_NEXT_KEY, res);
      // Clear main store so useMenuPlan re-adopts the current week
      clearPlan();
      void queryClient.invalidateQueries({ queryKey: MENU_LATEST_KEY });
      if (silent) return;
      toast.success(
        res.persisted
          ? "Menu de la semaine prochaine validé"
          : "Menu validé (enregistrement local uniquement)",
      );
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 401) return;
      else if (err instanceof ApiError && err.status === 409) {
        toast.error("Veuillez d'abord compléter votre profil");
        navigate("/onboarding");
      } else {
        toast.error((err as Error).message);
      }
    },
  });

  // Auto-validate when returning from reveal with ?validate=true
  useEffect(() => {
    if (!draftPlan) return;
    if (draftPlan.status === "validated") return;
    if (validate.isPending) return;
    const fromReveal = params.get("validate") === "true";
    if (fromReveal) {
      const draftSessionId = params.get("draft_session_id");
      setParams({}, { replace: true });
      validate.mutate({ draftSessionId, silent: false });
    }
  }, [params, draftPlan, validate.isPending, validate.mutate, setParams]);

  // Premium single-dinner swap (audit C1): keeps nextWeekMenuId and waits
  // for an explicit save instead of dropping the persisted menu id.
  const mealSwap = useMealSwap("next");

  // Show either the validated next-week plan or the draft being validated
  const plan = nextWeekPlan ?? draftPlan;
  const isValidated = plan?.status === "validated";

  return (
    <div className="flex h-full flex-col bg-background">
      <header className="flex items-center justify-between border-b border-hair px-5 py-4">
        <Link
          to="/dashboard"
          aria-label="Accueil"
          className="text-foreground hover:text-accent"
        >
          <ChevronLeft className="size-5" strokeWidth={1.25} />
        </Link>
        <Wordmark size="sm" />
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            aria-label="Filtres premium"
            className={cn(
              "relative flex items-center gap-1.5 text-foreground hover:text-accent",
              !isPremium && "text-ink-50",
            )}
          >
            <Sliders className="size-[18px]" strokeWidth={1.25} />
            {!isPremium && (
              <Sparkles className="size-3 text-accent" strokeWidth={2} />
            )}
            {countActive(filters) > 0 && (
              <span className="absolute -right-2 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[9px] font-semibold text-accent-foreground">
                {countActive(filters)}
              </span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setThemeSheetOpen(true)}
            aria-label="Choisir le thème de la semaine prochaine"
            className={cn(
              "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] uppercase tracking-[0.12em] transition-colors",
              theme && theme !== "surprise"
                ? "border-accent bg-accent/10 text-accent"
                : "border-hair text-ink-50 hover:border-ink-30",
            )}
          >
            <Sparkles className="size-3" strokeWidth={2} />
            {theme && theme !== "surprise" ? "Thème" : "Surprise"}
          </button>
          {brigade.running ? (
            <button
              aria-label="Annuler"
              onClick={brigade.abort}
              className="text-foreground hover:text-destructive"
            >
              <X className="size-[18px]" strokeWidth={1.25} />
            </button>
          ) : (
            <button
              aria-label="Régénérer la semaine prochaine"
              onClick={() => {
                clearPlan();
                void brigade.start({
                  premium: filters,
                  generation_mode: genMode,
                  theme,
                  week_start: getNextWeekStart(),
                });
              }}
              className="text-foreground hover:text-accent"
            >
              <RefreshCw className="size-[18px]" strokeWidth={1.25} />
            </button>
          )}
          {plan && (
            <button
              aria-label="Partager le menu"
              onClick={() => {
                const mid = useMenu.getState().nextWeekMenuId;
                if (!mid) {
                  toast.error(
                    "Validez d'abord votre menu avant de le partager.",
                  );
                  return;
                }
                void shareMenu(mid, "menu");
              }}
              className="text-foreground hover:text-accent"
            >
              <Share2 className="size-[18px]" strokeWidth={1.25} />
            </button>
          )}
        </div>
      </header>

      <PremiumFiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        initial={filters}
        onApply={(next) => {
          setFilters(next);
          toast.success("Filtres appliqués — appuyez sur ↻ pour régénérer.");
        }}
      />

      <ThemeSelectionSheet
        open={themeSheetOpen}
        onOpenChange={setThemeSheetOpen}
        onSelect={handleThemeSelect}
        currentTheme={theme}
      />

      {(brigade.running || brigade.error || brigade.usageRollup.calls > 0) && (
        <div className="border-b border-hair px-5 py-4">
          <BrigadeProgress
            currentRole={brigade.currentRole}
            completedRoles={brigade.completedRoles}
            error={brigade.error}
            usageByRole={brigade.usageByRole}
            usageRollup={brigade.usageRollup}
            usageSummary={brigade.usageSummary}
          />
        </div>
      )}

      {/* Semaine prochaine badge */}
      {!brigade.running && (
        <div className="border-b border-accent/20 bg-accent/5 px-5 py-2">
          <p className="text-[11px] uppercase tracking-[0.12em] text-accent">
            Semaine prochaine
          </p>
        </div>
      )}

      {!plan && !brigade.running && (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center">
          <Sparkles className="size-8 text-accent" strokeWidth={1.5} />
          <p className="font-serif text-[18px] italic">
            Pas encore de menu pour la semaine prochaine
          </p>
          <p className="text-[13px] text-ink-50">
            Appuyez sur ↻ pour générer votre menu.
          </p>
        </div>
      )}

      {plan && (
        <>
          {mealSwap.hasPendingSwap && (
            <SwapPendingBanner
              onSave={mealSwap.save}
              isSaving={mealSwap.isSaving}
            />
          )}
          <MenuHero plan={plan} validated={isValidated} />
          <MealList
            plan={plan}
            onSwap={mealSwap.swap}
            isSwapping={mealSwap.isSwapping}
            isFavorite={isFavorite}
            onToggleFav={toggleFav}
            onPaywall={() =>
              paywall.open({
                feature:
                  "Remplacer un repas avec l'IA fait partie de l'offre Cahier.",
              })
            }
          />
        </>
      )}
    </div>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────
function MenuHero({ plan, validated }: { plan: MenuPlan; validated: boolean }) {
  const totalKcal = plan.days.reduce((sum, d) => {
    const meals = Object.values(d.meals).filter(Boolean) as Recipe[];
    return sum + meals.reduce((mealSum, r) => mealSum + (r.calories ?? 0), 0);
  }, 0);
  const NUTRI_ORDER: Record<NutriScore, number> = {
    A: 0,
    B: 1,
    C: 2,
    D: 3,
    E: 4,
  };
  const worstScore = plan.days
    .flatMap((d) => Object.values(d.meals).filter(Boolean) as Recipe[])
    .reduce<NutriScore | null>((worst, r) => {
      if (!r.nutri_score) return worst;
      if (!worst) return r.nutri_score;
      return NUTRI_ORDER[r.nutri_score] > NUTRI_ORDER[worst]
        ? r.nutri_score
        : worst;
    }, null);
  return (
    <section className="border-b border-hair px-6 py-6">
      <div className="flex items-start justify-between gap-3">
        {plan.week_start && (
          <Eyebrow className="mb-2">
            {`Semaine ${weekNumber(plan.week_start)} · ${formatWeekRange(plan.week_start, plan.days.length)}`}
          </Eyebrow>
        )}
        {validated && (
          <span className="inline-flex items-center gap-1 rounded-pill bg-accent/15 px-2.5 py-1 text-[10.5px] uppercase tracking-[0.18em] text-accent">
            Validé
          </span>
        )}
      </div>
      <h1 className="h-display text-[40px] leading-[1] sm:text-[44px]">
        {plan.theme ? (
          <span className="italic">{plan.theme}</span>
        ) : (
          "Cuisine du moment"
        )}
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-4 text-[12px] text-ink-50">
        <span>{totalKcal.toLocaleString("fr-FR")} kcal</span>
        <span className="inline-flex items-center gap-1.5">
          <span
            className={cn("h-1.5 w-1.5 rounded-full", nutriColor(worstScore))}
          />
          Nutri {worstScore}
        </span>
        {plan.estimated_cost_eur != null && (
          <span>{formatPrice(plan.estimated_cost_eur)} estimés</span>
        )}
      </div>
    </section>
  );
}

// ─── Meal list ────────────────────────────────────────────────────────
function MealList({
  plan,
  onSwap,
  isSwapping,
  isFavorite,
  onToggleFav,
  onPaywall,
}: {
  plan: MenuPlan;
  onSwap: (day: string, slug: string) => void;
  isSwapping: boolean;
  isFavorite: (slug: string) => boolean;
  onToggleFav: (
    slug: string,
    kind?: "meal" | "batch",
    recipe?: Recipe,
  ) => Promise<void>;
  onPaywall: () => void;
}) {
  return (
    <div className="flex-1 overflow-auto px-5 py-4">
      <ul className="grid gap-3">
        {plan.days.map((d) => {
          const recipe = d.meals.dinner;
          if (!recipe) return null;
          return (
            <li key={d.day}>
              <MealCard
                date={d.day}
                recipe={recipe}
                onSwap={() => onSwap(d.day, recipe.slug)}
                disabled={isSwapping}
                isFav={isFavorite(recipe.slug)}
                onToggleFav={() => onToggleFav(recipe.slug, "meal", recipe)}
              />
            </li>
          );
        })}
      </ul>

      <button
        type="button"
        onClick={onPaywall}
        className="mt-4 flex w-full items-center gap-3 border border-dashed border-accent bg-accent/5 px-4 py-3.5 text-left transition-colors hover:bg-accent/10"
      >
        <Sparkles className="size-4 text-accent" strokeWidth={1.5} />
        <div className="flex-1 text-[12.5px] text-ink-70">
          <span className="font-serif italic text-accent">Premium · </span>
          Remplacer un repas avec l'IA, filtres Nutri / Éco, partage foyer
        </div>
        <span className="text-[12px] font-semibold text-accent">→</span>
      </button>

      <Link
        to="/shopping?week=next"
        className="mt-3 flex items-center gap-2 text-[12px] text-accent hover:underline"
      >
        Voir la liste de courses →
      </Link>
    </div>
  );
}

// ─── Meal card ────────────────────────────────────────────────────────
function MealCard({
  date,
  recipe,
  onSwap,
  disabled,
  isFav,
  onToggleFav,
}: {
  date: string;
  recipe: Recipe;
  onSwap: () => void;
  disabled?: boolean;
  isFav: boolean;
  onToggleFav: () => void;
}) {
  const { t } = useTranslation();
  const d = parseISODate(date);
  const totalMin = recipe.prep_minutes + recipe.cook_minutes;
  return (
    <article className="group relative flex items-stretch gap-3.5 border border-hair bg-background p-3 transition-colors hover:border-foreground">
      <Link
        to={`/recipe/${recipe.slug}`}
        className="flex flex-1 items-stretch gap-3.5"
      >
        <div className="flex w-14 flex-col items-center justify-center border-r border-hair pr-3 text-center">
          <div className="text-[10px] uppercase tracking-[0.2em] text-ink-50">
            {DAY_LABELS_SHORT[d.getDay()]}
          </div>
          <div className="font-serif text-2xl font-normal tracking-[-0.01em]">
            {d.getDate()}
          </div>
        </div>
        <div className="h-[70px] w-[70px] shrink-0 overflow-hidden rounded-sm bg-hair">
          <img
            src={getFoodImageUrl(recipe.slug)}
            alt={recipe.title}
            className="h-full w-full object-cover"
            loading="lazy"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 text-[9.5px] uppercase tracking-[0.18em] text-ink-50">
            {recipe.nutri_score ? `Nutri ${recipe.nutri_score}` : "—"}
          </div>
          <div className="font-serif text-[16px] leading-[1.15]">
            {recipe.title}
          </div>
          <div className="mt-1.5 flex gap-2 font-mono text-[11px] text-ink-50">
            <span>{totalMin}'</span>
            <span>·</span>
            <span>
              {recipe.servings}
              {t("reveal.servings_abbr")}
            </span>
          </div>
        </div>
      </Link>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onToggleFav();
        }}
        aria-label={isFav ? t("reveal.remove_fav") : t("reveal.add_fav")}
        className="absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full text-ink-50 opacity-0 transition-all hover:bg-accent/10 hover:text-accent group-hover:opacity-100"
      >
        <Heart
          className="size-3.5"
          strokeWidth={1.5}
          fill={isFav ? "currentColor" : "none"}
        />
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          onSwap();
        }}
        disabled={disabled}
        aria-label={t("reveal.replace_meal")}
        className="absolute right-10 top-2 flex h-8 w-8 items-center justify-center rounded-full text-ink-50 opacity-0 transition-opacity hover:bg-accent/10 hover:text-accent group-hover:opacity-100 disabled:opacity-50"
      >
        <RefreshCw
          className={cn("size-3.5", disabled && "animate-spin")}
          strokeWidth={1.5}
        />
      </button>
    </article>
  );
}

function weekNumber(iso: string | Date): number {
  const d = typeof iso === "string" ? parseISODate(iso) : iso;
  const onejan = new Date(d.getFullYear(), 0, 1);
  return Math.ceil(
    ((d.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7,
  );
}

function countActive(p: PremiumFilters | null): number {
  if (!p) return 0;
  return (
    Number(Boolean(p.nutri_min)) +
    Number(Boolean(p.eco_min)) +
    Number(Boolean(p.seasonal_only)) +
    Number(typeof p.max_kcal_per_meal === "number")
  );
}
